/**
 * /{ws}/mail — Gmail-style mail (core MailboxViewModel), every mailbox I'm a member of in one list unless the filter row
 * picks one: a rail with Compose and the folders; the inbox's
 * tabs as the server defines them (MailTabDef: Priority, then the categories All · Clients · Team · Promotions ·
 * Updates today, each with "N new" and the newest unread sender — subject; Mail opens on Priority unless it's empty); the thread list (sender avatar, unread rows bold
 * with a dot, a tab tag where the tab isn't obvious) with star, the Priority marker, label chips and snooze / scheduled
 * times, hover actions (archive, delete, read/unread) and a menu (Mark as priority / Not priority, snooze, label as,
 * spam, move to tab); Gmail's views (starred, snoozed, scheduled), my drafts and the mailbox's labels;
 * search across the mailbox. A row opens the thread (/{ws}/mail/t/{id}) beside the list, like a chat thread. The pane's
 * own width picks the layout: in a narrow pane (a phone, a thread open beside the list) the rows stack, the tabs fold,
 * and without the sidebar's folders the rail folds into a folder picker and compose / contacts buttons.
 */
import type { MailLabel, MailTabDef, MailTabId } from "@bundle/contracts";
import type { MailFreshness, MailThreadRow, MailView, MailboxViewModel, MessageKey } from "@bundle/core";
import { MailQuery, MailSearchViewModel, Roles, Shell, mailUpdatedText } from "@bundle/core";
import * as Mail from "@bundle/core/logic/mail";
import {
  Avatar,
  Badge,
  Button,
  CategoryTabs,
  Checkbox,
  Chip,
  ComingSoon,
  Divider,
  Icon,
  IconButton,
  MAIL_FOLDER_ICONS,
  Menu,
  MAIL_VIEWS,
  labelTone,
  mailTabIcon,
  mailTabTone,
  ThreadProjectChip,
  useThreadProject,
  Pressable,
  ScrollView,
  Select,
  Skeleton,
  Spinner,
  Stack,
  Text,
  mailShortDate,
  openComposer,
  showMailUndo,
  useFormat,
  useLocale,
  useMail,
  useNavigation,
  useOwned,
  usePaneWidthAtLeast,
  useT,
  useViewModel,
  useWorkspace,
  useFeatureAccess,
  type MenuEntry,
  AnimatedList,
  NewItemsPill,
  Progress,
  MailCodeChip,
} from "@bundle/ui";
import { useEffect, useId, useRef, useState } from "react";
import { MailFilterRow } from "./MailFilterRow.tsx";
import { MailSearchBox } from "./MailSearchBox.tsx";
import { useListAnchor } from "./useListAnchor.ts";
import { labelEntries, snoozeEntries } from "./mailMenus.ts";
import { PriorityMarker, markPriority, noReplyDays } from "./priority.tsx";
import { useMailShortcuts } from "./useMailShortcuts.ts";

/** Gmail's one-line rows need this much list width for the sender, the subject and the date side by side. */
const WIDE_LIST = 720;
/** The folder rail's width (size token `tab.maxWidth`). */
const RAIL = 220;

type Props = {
  /** The Mailbox filter: one mailbox, or a group's address; neither = all of mine. */
  mailboxId?: string | undefined;
  group?: string | undefined;
  folder?: MailView | undefined;
  /** The route's tab; none = Mail picks (Priority unless it's empty). */
  tab?: MailTabId | undefined;
  /** One of the mailbox's labels (its conversations across folders). */
  label?: string | undefined;
  /** The search in the box (a people card's "Recent emails" opens one: `with:` the people). */
  q?: string | undefined;
  /** The filter row's chips (MailQuery.mailListFilterQuery). */
  filter?: string | undefined;
  /** The sidebar shows the mail folders (the Email view), so this screen hides its own rail. */
  railInSidebar?: boolean | undefined;
};

export function MailScreen(p: Props) {
  const t = useT();
  const mail = useMail();
  if (mail === undefined) return <ComingSoon icon="mail" title={t("mail.title")} body={t("mail.noMailbox.body")} />;
  return <MailView vm={mail.mailbox} {...p} />;
}

function MailView({ vm, mailboxId, group, folder, tab, label, q, filter, railInSidebar: inSidebar }: Props & { vm: MailboxViewModel }) {
  const t = useT();
  const nav = useNavigation();
  const ws = useWorkspace();
  const s = useViewModel(vm);
  const locale = useLocale();
  // The open tab's definition, and where an empty Priority sends me (the first category).
  const tabDef = s.tabDefs.find((d) => d.id === s.tab);
  const firstCategory = Mail.categoryTabs(s.tabDefs)[0];
  // Email switched off while owners and admins may still look (Settings → Features): no Compose.
  const canSend = useFeatureAccess()("mail") === "full";
  const searchId = useId();
  const searchBox = useOwned(`mail-search:${ws.vm.wsId}:${searchId}`, () => new MailSearchViewModel(vm, ws.vm));
  const query = useViewModel(searchBox).query;
  // Gmail's keyboard cursor (j / k), the row the single-key shortcuts act on.
  const [cursor, setCursor] = useState(0);
  // The list follows the cursor only after j / k moved it, until I scroll with the mouse or a finger: a resize (rows
  // restacking) or a remount must not pull the list back to the cursor's row.
  const [followCursor, setFollowCursor] = useState(false);
  useEffect(() => {
    if (!followCursor) return undefined;
    const off = () => setFollowCursor(false);
    const opts = { passive: true } as const;
    window.addEventListener("wheel", off, opts);
    window.addEventListener("touchmove", off, opts);
    return () => {
      window.removeEventListener("wheel", off);
      window.removeEventListener("touchmove", off);
    };
  }, [followCursor]);
  // The URL decides the mailbox / folder / tab, the search and the filter row: a layout change or Back from a thread
  // remounts this screen on the same URL, and it comes back as it was (Gmail keeps the search in its URL too).
  useEffect(() => {
    const before = vm.state.query;
    vm.show({ mailboxId, group, folder, tab, label, query: q ?? "", filters: MailQuery.mailListFilters(filter ?? "") });
    // The box keeps what's being typed unless the route brought another search.
    if (searchBox.state.query === "" || (q ?? "") !== before) searchBox.load(q ?? "");
  }, [vm, searchBox, mailboxId, group, folder, tab, label, q, filter]);
  // Mail came on screen, or the app came back: it catches up where I can see it.
  useEffect(() => {
    vm.freshen();
    const back = () => {
      if (document.visibilityState === "visible") vm.freshen();
    };
    document.addEventListener("visibilitychange", back);
    return () => document.removeEventListener("visibilitychange", back);
  }, [vm]);
  const listRef = useRef<HTMLDivElement>(null);
  // The list comes back on the same mail after a remount (a layout change, Back from a thread), whatever the rows' height.
  const listKey = Shell.formatUrl({ type: "mail", ws: ws.slug, ...(mailboxId === undefined ? {} : { mailboxId }), ...(group === undefined ? {} : { group }), ...(folder === undefined ? {} : { folder }), ...(tab === undefined ? {} : { tab }), ...(label === undefined ? {} : { label }), ...(q === undefined ? {} : { q }), ...(filter === undefined ? {} : { filter }) });
  useListAnchor(listKey, listRef, s.rows.map((r) => r.id), listRow);
  const [atTop, setAtTop] = useState(true);
  const onAtTop = (top: boolean) => {
    if (top === atTop) return;
    setAtTop(top);
    vm.setAtTop(top);
  };
  // Search as you type (debounced).
  useEffect(() => {
    if (query === vm.state.query) return;
    const h = setTimeout(() => vm.search(query), 100);
    return () => clearTimeout(h);
  }, [query, vm]);
  // The search and the filter row go into the URL as they change (replacing it, so Back leaves Mail, not a keystroke).
  const listFilter = MailQuery.mailListFilterQuery(s.filters);
  useEffect(() => {
    if (!nav.focused) return;
    const now = vm.state;
    const wantQ = now.query.trim() === "" ? undefined : now.query;
    const wantFilter = MailQuery.mailListFilterQuery(now.filters) || undefined;
    if (wantQ === (q || undefined) && wantFilter === (filter || undefined)) return;
    const route: Shell.Route = { type: "mail", ws: ws.slug, ...scope, ...(folder === undefined ? {} : { folder }), ...(tab === undefined ? {} : { tab }), ...(label === undefined ? {} : { label }) };
    if (wantQ !== undefined) route.q = wantQ;
    if (wantFilter !== undefined) route.filter = wantFilter;
    nav.replace(Shell.formatUrl(route));
    // Written from the view model's state, not the route.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [s.query, listFilter]);

  // The room the list really has decides its layout, not the window: on a wide screen the pane can still be narrow (a
  // thread open beside the list, several panes). The rail only shows with room for it and a wide list.
  const railInSidebar = inSidebar === true;
  const roomForRail = usePaneWidthAtLeast(RAIL + WIDE_LIST);
  const roomForRows = usePaneWidthAtLeast(WIDE_LIST);
  const showRail = nav.canSplit && !railInSidebar && roomForRail;
  // Neither the sidebar nor the rail shows the folders: the toolbar gets the folder picker, Contacts and Compose.
  const foldersInToolbar = !showRail && !(nav.canSplit && railInSidebar);
  // One-line rows (Gmail's wide list) or stacked rows (avatar; sender and date; subject; snippet).
  const wide = showRail || roomForRows;
  // Where the Mailbox filter stands: it stays in the route as the folder and tab change.
  const scope = { ...(mailboxId === undefined ? {} : { mailboxId }), ...(group === undefined ? {} : { group }) };
  const go = (r: { scope?: { mailboxId?: string; group?: string }; folder?: MailView; tab?: MailTabId; label?: string; filter?: string }) => {
    const route: Shell.Route = { type: "mail", ws: ws.slug, ...(r.scope ?? scope) };
    if (r.folder !== undefined && r.folder !== "inbox") route.folder = r.folder;
    if (r.tab !== undefined) route.tab = r.tab;
    if (r.label !== undefined) route.label = r.label;
    if (r.filter !== undefined && r.filter !== "") route.filter = r.filter;
    searchBox.clear();
    nav.replace(Shell.formatUrl(route));
  };
  const keepView = { ...(s.folder === "inbox" ? {} : { folder: s.folder }), ...(tab === undefined ? {} : { tab }), ...(label === undefined ? {} : { label }) };
  const clearFilters = () => {
    vm.clearFilters();
    go({ scope: {}, ...keepView });
  };
  const compose = () => {
    if (canSend && s.mailboxId !== undefined) openComposer({ mailboxId: s.mailboxId, mode: "new", to: [], cc: [], subject: "", text: "" });
  };
  const contactsUrl = Shell.formatUrl({ type: "contacts", ws: ws.slug });
  const at = s.rows[Math.min(cursor, s.rows.length - 1)];
  const undoKey = { archive: "mail.undo.archived", trash: "mail.undo.trashed", spam: "mail.undo.spam", inbox: "mail.undo.inbox" } as const;
  useMailShortcuts(nav.focused, (k) => {
    const ids = s.selected.length > 0 ? s.selected : at === undefined ? [] : [at.id];
    const move = (to: "archive" | "trash" | "spam") => {
      if (ids.length === 0 || !canSend) return;
      showMailUndo(t(undoKey[to], { count: ids.length }), s.selected.length > 0 ? vm.moveSelected(to) : to === "archive" ? vm.archive(ids[0]!) : to === "trash" ? vm.trash(ids[0]!) : vm.markSpam(ids[0]!, true));
    };
    switch (k) {
      case "compose":
        compose();
        return true;
      case "search":
        (document.querySelector('[data-testid="mail-search"], [data-testid="mail-search-phone"]') as HTMLInputElement | null)?.focus();
        return true;
      case "next":
        setFollowCursor(true);
        setCursor(Math.min(cursor + 1, Math.max(0, s.rows.length - 1)));
        return true;
      case "prev":
        setFollowCursor(true);
        setCursor(Math.max(cursor - 1, 0));
        return true;
      case "open":
        if (at !== undefined) {
          searchBox.remember();
          nav.open(Shell.formatUrl({ type: "mailThread", ws: ws.slug, threadId: at.id }), nav.canSplit ? "split" : "current");
        }
        return at !== undefined;
      case "archive":
        move("archive");
        return true;
      case "trash":
        move("trash");
        return true;
      case "spam":
        move("spam");
        return true;
      case "star":
        if (at !== undefined && canSend) vm.setStarred(at.id, !at.starred);
        return at !== undefined;
      case "priority":
      case "notPriority":
        markPriority(t, vm, s.threads.filter((x) => ids.includes(x.id)), k === "priority", s.selected.length > 0);
        return ids.length > 0;
      case "select":
        if (at !== undefined) vm.toggleSelected(at.id);
        return at !== undefined;
      case "markRead":
      case "markUnread":
        if (!canSend) return true;
        if (s.selected.length > 0) vm.setSelectedUnread(k === "markUnread");
        else if (at !== undefined) vm.setUnread(at.id, k === "markUnread");
        return true;
      default:
        return false;
    }
  });
  const inboxUnread = Mail.mailInboxUnread(s.mailboxes, mailboxId);
  const searching = s.query.trim() !== "";
  const filtered = group !== undefined || mailboxId !== undefined || MailQuery.hasListFilters(s.filters);
  const placeholder = t(s.unified ? "mail.search.all" : "mail.search.placeholder");
  const labelRows = Mail.mergedMailLabels(s.labels);
  // Pills say which mailbox a row is from: only where the list mixes them.
  const showPills = s.unified && group === undefined;
  const meMember = ws.state.cache?.members[ws.state.meId];
  const admin = meMember !== undefined && Roles.isManager(meMember, ws.vm.deps.clock.now());

  if (s.status === "empty") {
    return (
      <Stack grow align="center" justify="center" gap="3" padding="8" testID="mail-empty-mailbox">
        <Icon name="mail" size="icon.lg" color="text.tertiary" />
        <Text variant="title3" weight="semibold" align="center">
          {t("mail.noMailbox.title")}
        </Text>
        <Text color="text.secondary" align="center">
          {admin ? t("mail.noMailbox.adminBody") : t("mail.noMailbox.body")}
        </Text>
        {admin ? <Button variant="primary" icon="settings" label={t("mail.noMailbox.setup")} onPress={() => nav.open(Shell.formatUrl({ type: "settings", ws: ws.slug, section: "mail" }))} testID="mail-setup" /> : null}
      </Stack>
    );
  }

  const rail = (
    <Stack width="tab.maxWidth" gap="4" padding="3" background="bg.sidebar" testID="mail-rail">
      {canSend ? <Button label={t("mail.compose")} icon="edit" onPress={compose} disabled={s.mailboxId === undefined} testID="mail-compose" /> : null}
      <Stack gap="0.5">
        {MAIL_VIEWS.map((f) => (
          <Pressable key={f} direction="horizontal" radius="md" selected={!searching && s.label === undefined && s.folder === f} onPress={() => go({ folder: f })} testID={`mail-folder-${f}`}>
            <Stack grow direction="horizontal" align="center" gap="2" paddingX="2" paddingY="1.5">
              <Icon name={MAIL_FOLDER_ICONS[f]} size="icon.sm" color="text.secondary" />
              <Stack grow>
                <Text variant="footnote" weight={f === "inbox" && inboxUnread > 0 ? "semibold" : "regular"} lines={1}>
                  {t(`mail.folder.${f}`)}
                </Text>
              </Stack>
              {f === "inbox" && inboxUnread > 0 ? <Badge count={inboxUnread} tone="neutral" /> : null}
            </Stack>
          </Pressable>
        ))}
      </Stack>
      {labelRows.length > 0 ? (
        <Stack gap="0.5" testID="mail-labels">
          <Text variant="caption" weight="semibold" color="text.tertiary">
            {t("mail.labels")}
          </Text>
          {labelRows.map((l) => (
            <Pressable key={l.id} direction="horizontal" radius="md" selected={!searching && s.label !== undefined && l.ids.includes(s.label)} onPress={() => go({ label: l.id })} testID={`mail-label-${l.id}`}>
              <Stack grow direction="horizontal" align="center" gap="2" paddingX="2" paddingY="1.5">
                <Icon name="tag" size="icon.sm" color="text.secondary" />
                <Text variant="footnote" lines={1}>
                  {l.name}
                </Text>
              </Stack>
            </Pressable>
          ))}
        </Stack>
      ) : null}
      <Pressable direction="horizontal" radius="md" onPress={() => nav.open(contactsUrl)} testID="mail-contacts">
        <Stack grow direction="horizontal" align="center" gap="2" paddingX="2" paddingY="1.5">
          <Icon name="users" size="icon.sm" color="text.secondary" />
          <Text variant="footnote">{t("nav.contacts")}</Text>
        </Stack>
      </Pressable>
    </Stack>
  );

  return (
    <Stack grow direction="horizontal" testID="mail-screen">
      {/* The sidebar's Email view (activity bar) carries the same folders, tabs and mailboxes while it shows. */}
      {showRail ? rail : null}
      <Stack grow>
        {!foldersInToolbar ? null : (
          <Stack paddingX="3" paddingY="2">
            <MailSearchBox vm={searchBox} placeholder={placeholder} testID="mail-search-phone" />
          </Stack>
        )}
        <Stack direction="horizontal" align="center" gap="2" paddingX="4" paddingY="2">
          <Stack grow>
            {foldersInToolbar ? <Stack /> : null}
            {foldersInToolbar ? null : <MailSearchBox vm={searchBox} placeholder={placeholder} testID="mail-search" />}
          </Stack>
          {!foldersInToolbar ? null : (
            <>
              <Select
                accessibilityLabel={t("mail.folders")}
                value={s.folder}
                onChange={(f) => go({ folder: f })}
                options={MAIL_VIEWS.map((f) => ({ value: f, label: t(`mail.folder.${f}`), icon: MAIL_FOLDER_ICONS[f] }))}
                testID="mail-folder-select"
              />
              <IconButton icon="users" accessibilityLabel={t("nav.contacts")} onPress={() => nav.open(contactsUrl)} testID="mail-contacts-phone" />
              {canSend ? <IconButton icon="edit" variant="filled" accessibilityLabel={t("mail.compose")} onPress={compose} testID="mail-compose-phone" /> : null}
            </>
          )}
          <IconButton icon="reload" accessibilityLabel={t("mail.retry")} onPress={() => vm.retry()} testID="mail-reload" />
        </Stack>
        <Divider />
        {canSend && s.rows.length > 0 && s.folder !== "drafts" ? <BulkBar vm={vm} /> : null}
        {!searching && s.folder === "inbox" && s.label === undefined ? (
          <CategoryTabs<MailTabId>
            tabs={Mail.stripTabs(s.tabDefs).map((def) => {
              const x: { newCount: number; preview?: { sender: string; subject: string } | undefined } = (def.kind === "priority" ? s.priority : s.tabs.find((c) => c.tab === def.id)) ?? { newCount: 0 };
              return {
                value: def.id,
                label: Mail.mailTabLabel(def, locale),
                icon: mailTabIcon(def),
                emphasized: x.newCount > 0,
                ...(x.newCount > 0 ? { pill: { label: t("mail.tab.new", { count: x.newCount }), tone: mailTabTone(def), count: x.newCount } } : {}),
                preview: Mail.mailTabPreview(x.preview),
              };
            })}
            value={s.tab}
            onChange={(x) => {
              // The list switches now (kept list or skeleton); the URL catches up (its show() is then a no-op).
              vm.setTab(x);
              go({ tab: x });
            }}
            accessibilityLabel={t("mail.tabs")}
            testID="mail-tabs"
          />
        ) : null}
        {s.folder === "drafts" && !searching && s.label === undefined ? null : <MailFilterRow vm={vm} pick={(to) => go({ scope: to, ...keepView, filter: listFilter })} clear={clearFilters} />}
        <MailUpdateStrip freshness={s.freshness} lastUpdatedAt={s.lastUpdatedAt} now={ws.vm.deps.clock.now()} />
        {s.status === "loading" ? (
          <MailListSkeleton />
        ) : s.status === "failed" ? (
          <Stack grow align="center" justify="center" gap="3" padding="8" testID="mail-failed">
            <Text color="text.secondary" align="center">
              {t("mail.loadFailed")}
            </Text>
            <Button variant="secondary" onPress={() => vm.retry()} label={t("mail.retry")} testID="mail-retry" />
          </Stack>
        ) : s.folder === "drafts" && !searching && s.label === undefined ? (
          <DraftsList vm={vm} wide={wide} />
        ) : s.rows.length === 0 && filtered && !searching ? (
          <Stack grow align="center" justify="center" gap="2" padding="8" testID="mail-empty-filters">
            <Text variant="headline" weight="semibold" align="center">
              {t("mail.filter.empty")}
            </Text>
            <Button variant="secondary" size="sm" label={t("mail.filter.clearAll")} onPress={clearFilters} testID="mail-empty-clear" />
          </Stack>
        ) : s.rows.length === 0 && !searching && s.folder === "inbox" && s.label === undefined && tabDef?.kind === "priority" ? (
          <Stack grow align="center" justify="center" gap="3" padding="8" testID="mail-empty-priority">
            <Icon name="checkCircle" size="icon.lg" color="text.tertiary" />
            <Text variant="title3" weight="semibold" align="center">
              {t("mail.empty.priority.title")}
            </Text>
            <Text color="text.secondary" align="center">
              {t("mail.empty.priority.body")}
            </Text>
            {firstCategory === undefined ? null : (
              <Button
                variant="secondary"
                label={t("mail.empty.priority.goTab", { tab: Mail.mailTabLabel(firstCategory, locale) })}
                onPress={() => go({ tab: firstCategory.id })}
                testID="mail-empty-priority-email"
              />
            )}
          </Stack>
        ) : s.rows.length === 0 ? (
          <Stack grow align="center" justify="center" gap="2" padding="8" testID="mail-empty">
            <Icon name={searching ? "search" : s.folder === "inbox" ? mailTabIcon(tabDef) : MAIL_FOLDER_ICONS[s.folder]} size="icon.lg" color="text.tertiary" />
            <Text color="text.secondary" align="center">
              {searching ? t("mail.empty.search", { query: s.query }) : s.folder === "inbox" ? t("mail.empty.tab", { tab: tabDef === undefined ? "" : Mail.mailTabLabel(tabDef, locale) }) : t("mail.empty.folder")}
            </Text>
          </Stack>
        ) : (
          <ScrollView onEndReached={() => void vm.loadMore()} onAtTopChange={onAtTop} scrollRef={listRef} testID="mail-list">
            {atTop || s.newIds.length === 0 ? null : <NewItemsPill label={t("mail.tab.new", { count: s.newIds.length })} onPress={() => listRef.current?.scrollTo({ top: 0, behavior: reduceMotion() ? "auto" : "smooth" })} testID="mail-new-pill" />}
            <AnimatedList key={`${s.folder}:${s.tab}:${s.label ?? ""}:${s.query}`}>
              {s.rows.map((row) => (
                <ThreadRow key={row.id} vm={vm} row={row} folder={s.folder} labels={s.labels} wide={wide} pills={Mail.mailRowPills(row.thread, s.mailboxes, showPills)} onOpen={() => searchBox.remember()} selected={s.selected.includes(row.id)} cursor={nav.focused && row.id === at?.id} follow={followCursor} arriving={s.newIds.includes(row.id)} />
              ))}
            </AnimatedList>
            {!searching && s.folder === "inbox" && s.label === undefined && tabDef?.kind === "priority" && s.nextCursor === undefined ? (
              <Stack paddingX="4" paddingY="3">
                <Text variant="caption" color="text.tertiary">
                  {t("mail.priority.footer")}
                </Text>
              </Stack>
            ) : null}
            {s.loadingMore ? (
              <Stack align="center" padding="3">
                <Spinner size="icon.sm" />
              </Stack>
            ) : null}
          </ScrollView>
        )}
      </Stack>
    </Stack>
  );
}

/** A row of the list, by thread id (ThreadRow's test id). */
const listRow = (list: HTMLElement, id: string) => list.querySelector(`[data-testid="mail-row-${CSS.escape(id)}"]`);

const reduceMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;

/** How a refresh of the list on screen goes: a thin line once it's taken a moment, then a caption (never over the rows). */
function MailUpdateStrip({ freshness, lastUpdatedAt, now }: { freshness: MailFreshness; lastUpdatedAt: number | undefined; now: number }) {
  const t = useT();
  if (freshness === "updating") return <Progress accessibilityLabel={t("mail.updating")} testID="mail-updating" />;
  if ((freshness !== "updated" && freshness !== "stale") || lastUpdatedAt === undefined) return null;
  const text = mailUpdatedText(lastUpdatedAt, now);
  return (
    <Stack paddingX="4" paddingY="1" testID="mail-updated">
      <Text variant="caption" color="text.tertiary" lines={1}>
        {t(text.key as MessageKey, text.params as never)}
      </Text>
    </Stack>
  );
}

/** A list not seen yet (Gmail's): rows shaped like conversations until it arrives, never the list I left. */
function MailListSkeleton() {
  return (
    <Stack grow testID="mail-loading">
      {Array.from({ length: 8 }, (_, i) => (
        <Stack key={i} direction="horizontal" align="center" gap="3" paddingX="4" paddingY="3">
          <Skeleton shape="circle" size="avatar.lg" />
          <Stack grow gap="1">
            <Skeleton variant="footnote" width="picker.width" />
            <Skeleton variant="caption" lines={2} />
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}

/**
 * Gmail's list toolbar: the select-all checkbox, then — with conversations ticked — how many, and archive / delete /
 * spam (with Undo), read / unread, label.
 */
function BulkBar({ vm }: { vm: MailboxViewModel }) {
  const t = useT();
  const s = useViewModel(vm);
  const n = s.selected.length;
  const all = n > 0 && n === s.rows.length;
  const away = s.folder === "archive" || s.folder === "trash" || s.folder === "spam";
  const picked = s.threads.filter((x) => s.selected.includes(x.id));
  const move = (folder: "archive" | "trash" | "spam" | "inbox", key: "mail.undo.archived" | "mail.undo.trashed" | "mail.undo.spam" | "mail.undo.inbox") => showMailUndo(t(key, { count: n }), vm.moveSelected(folder));
  return (
    <Stack direction="horizontal" align="center" gap="1" paddingX="2" paddingY="0.5" testID="mail-bulk">
      <Checkbox compact accessibilityLabel={t("mail.selectAll")} value={all} indeterminate={n > 0 && !all} onChange={() => vm.toggleAllSelected()} testID="mail-select-all" />
      {n === 0 ? null : (
        <>
          <Text variant="footnote" color="text.secondary">
            {t("mail.selectedCount", { count: n })}
          </Text>
          {away ? (
            <IconButton icon="inbox" size="sm" accessibilityLabel={t("mail.action.moveToInbox")} onPress={() => move("inbox", "mail.undo.inbox")} testID="mail-bulk-inbox" />
          ) : (
            <IconButton icon="archive" size="sm" accessibilityLabel={t("mail.action.archive")} onPress={() => move("archive", "mail.undo.archived")} testID="mail-bulk-archive" />
          )}
          <IconButton icon="trash" size="sm" accessibilityLabel={t("mail.action.trash")} onPress={() => move("trash", "mail.undo.trashed")} testID="mail-bulk-trash" />
          {s.folder === "spam" ? null : <IconButton icon="ban" size="sm" accessibilityLabel={t("mail.action.spam")} onPress={() => move("spam", "mail.undo.spam")} testID="mail-bulk-spam" />}
          <IconButton icon="priority" size="sm" color="status.warning" accessibilityLabel={t("mail.action.markPriority")} onPress={() => markPriority(t, vm, picked, true, true)} testID="mail-bulk-priority" />
          <IconButton icon="priority" size="sm" color="text.tertiary" accessibilityLabel={t("mail.action.notPriority")} onPress={() => markPriority(t, vm, picked, false, true)} testID="mail-bulk-not-priority" />
          <IconButton icon="check" size="sm" accessibilityLabel={t("mail.action.markRead")} onPress={() => vm.setSelectedUnread(false)} testID="mail-bulk-read" />
          <IconButton icon="mail" size="sm" accessibilityLabel={t("mail.action.markUnread")} onPress={() => vm.setSelectedUnread(true)} testID="mail-bulk-unread" />
          {s.labels.length > 0 ? (
            <Menu items={Mail.mergedMailLabels(s.labels).map((l) => ({ id: `bulk-label-${l.id}`, label: t("mail.action.labelAs", { label: l.name }), icon: "tag" as const, onSelect: () => vm.toggleSelectedLabel(l.id) }))} testID="mail-bulk-labels">
              <IconButton icon="tag" size="sm" accessibilityLabel={t("mail.labels")} onPress={() => undefined} testID="mail-bulk-labels-button" />
            </Menu>
          ) : null}
          <Stack grow />
          <IconButton icon="close" size="sm" accessibilityLabel={t("mail.clearSelection")} onPress={() => vm.clearSelection()} testID="mail-bulk-clear" />
        </>
      )}
    </Stack>
  );
}

/** My drafts (Gmail's Drafts): a row reopens the composer on it, the trash discards it. */
function DraftsList({ vm, wide }: { vm: MailboxViewModel; wide: boolean }) {
  const t = useT();
  const locale = useLocale();
  const s = useViewModel(vm);
  if (s.drafts.length === 0) {
    return (
      <Stack grow align="center" justify="center" gap="2" padding="8" testID="mail-drafts-empty">
        <Icon name="edit" size="icon.lg" color="text.tertiary" />
        <Text color="text.secondary" align="center">
          {t("mail.drafts.empty")}
        </Text>
      </Stack>
    );
  }
  return (
    <ScrollView testID="mail-drafts">
      {s.drafts.map((d) => (
        <Stack key={d.id} direction="horizontal" align="center" gap="2" paddingX="3" hoverGroup testID={`mail-draft-${d.id}`}>
          <Stack grow>
            <Pressable direction="horizontal" onPress={() => openComposer({ mailboxId: d.mailboxId, mode: d.mode, to: [], cc: [], subject: d.subject, text: d.text, draft: d })} accessibilityLabel={d.subject === "" ? t("mail.noSubject") : d.subject} testID={`mail-draft-open-${d.id}`}>
              <Stack grow direction={wide ? "horizontal" : "vertical"} align={wide ? "center" : "stretch"} gap={wide ? "3" : "0.5"} paddingY="2">
                <Stack width={wide ? "property.label" : undefined}>
                  <Text weight="semibold" color="status.danger.fg" lines={1}>
                    {t("mail.draft.label")}
                  </Text>
                </Stack>
                <Stack grow>
                  <Text lines={1}>{`${d.subject === "" ? t("mail.noSubject") : d.subject}${d.to === "" ? "" : ` — ${d.to}`}`}</Text>
                </Stack>
                <Stack fixedSize>
                  <Text variant="footnote" color="text.secondary" lines={1}>
                    {mailShortDate(d.updatedAt, locale)}
                  </Text>
                </Stack>
              </Stack>
            </Pressable>
          </Stack>
          <IconButton icon="trash" size="sm" accessibilityLabel={t("mail.draft.discard")} onPress={() => vm.discardDraft(d.id)} testID={`mail-draft-discard-${d.id}`} />
        </Stack>
      ))}
    </ScrollView>
  );
}

function ThreadRow({ vm, row, folder, labels: allLabels, wide, pills, onOpen, selected, cursor, follow, arriving }: { vm: MailboxViewModel; row: MailThreadRow; folder: MailView; labels: MailLabel[]; wide: boolean; pills: Mail.MailRowPill[]; onOpen: () => void; selected: boolean; cursor: boolean; follow: boolean; arriving: boolean }) {
  const t = useT();
  const fmt = useFormat();
  const nav = useNavigation();
  const ws = useWorkspace();
  const now = ws.vm.deps.clock.now();
  const locale = useLocale();
  // Email look-only: the row opens, nothing on it changes the thread.
  const canChange = useFeatureAccess()("mail") === "full";
  const id = row.id;
  // Labels belong to a mailbox: the row offers its own.
  const labels = allLabels.filter((l) => l.mailboxId === row.thread.mailboxId);
  // Like a chat thread: beside the list where panes split, in its place otherwise.
  const open = () => {
    onOpen();
    nav.open(Shell.formatUrl({ type: "mailThread", ws: ws.slug, threadId: id }), nav.canSplit ? "split" : "current");
  };
  const newTab = {
    id: "new-tab",
    label: t("shell.openInNewTab"),
    icon: "tabs" as const,
    onSelect: () => {
      onOpen();
      nav.open(Shell.formatUrl({ type: "mailThread", ws: ws.slug, threadId: id }), "newTab");
    },
  };
  const hover = [
    folder === "archive" || folder === "trash" || folder === "spam"
      ? { id: "inbox", label: t("mail.action.moveToInbox"), icon: "inbox" as const, onSelect: () => showMailUndo(t("mail.undo.inbox", { count: 1 }), vm.moveToInbox(id)) }
      : { id: "archive", label: t("mail.action.archive"), icon: "archive" as const, onSelect: () => showMailUndo(t("mail.undo.archived", { count: 1 }), vm.archive(id)) },
    { id: "trash", label: t("mail.action.trash"), icon: "trash" as const, onSelect: () => showMailUndo(t("mail.undo.trashed", { count: 1 }), vm.trash(id)) },
    row.unread
      ? { id: "read", label: t("mail.action.markRead"), icon: "check" as const, onSelect: () => vm.setUnread(id, false) }
      : { id: "unread", label: t("mail.action.markUnread"), icon: "mail" as const, onSelect: () => vm.setUnread(id, true) },
  ];
  const inPriority = row.priority !== undefined;
  const togglePriority = (on: boolean) => markPriority(t, vm, [row.thread], on, false);
  const more: MenuEntry[] = [
    inPriority
      ? { id: "not-priority", label: t("mail.action.notPriority"), icon: "priority", onSelect: () => togglePriority(false) }
      : { id: "priority", label: t("mail.action.markPriority"), icon: "priority", onSelect: () => togglePriority(true) },
    { id: "sep-priority", separator: true },
    ...snoozeEntries(t, fmt.dateTime, now, row.thread.snoozedUntil, (until) => vm.snooze(id, until)),
    { id: "sep-snooze", separator: true },
    ...labelEntries(t, labels, row.thread.labelIds ?? [], (ids) => vm.setLabels(id, ids)),
    ...(labels.length > 0 ? [{ id: "sep-labels", separator: true } as const] : []),
    folder === "spam"
      ? { id: "not-spam", label: t("mail.action.notSpam"), icon: "inbox", onSelect: () => showMailUndo(t("mail.undo.inbox", { count: 1 }), vm.markSpam(id, false)) }
      : { id: "spam", label: t("mail.action.spam"), icon: "ban", onSelect: () => showMailUndo(t("mail.undo.spam", { count: 1 }), vm.markSpam(id, true)) },
    { id: "sep", separator: true },
    ...Mail.categoryTabs(vm.state.tabDefs)
      .filter((x) => x.id !== row.thread.tab)
      .map((x) => ({
        id: `tab-${x.id}`,
        label: `${t("mail.action.moveToTab")}: ${Mail.mailTabLabel(x, locale)}`,
        icon: mailTabIcon(x),
        onSelect: () => vm.moveToTab(id, x.id),
      })),
  ];
  const weight = row.unread ? "bold" : "regular";
  const name = row.senders === "" ? t("mail.me") : row.senders;
  const first = row.thread.participants[0];
  const avatarSrc = first === undefined ? undefined : Mail.mailAvatarPath(ws.vm.wsId, first.address);
  // The tab where it isn't obvious: Clients outside its tab; any but uncategorised in Priority and All (they gather every tab).
  const gathers = folder === "inbox" && Mail.tabGathers(vm.state.tabDefs.find((d) => d.id === vm.state.tab));
  const tagId = gathers ? (row.thread.tab === "email" ? undefined : row.thread.tab) : row.thread.tab === "clients" && (folder !== "inbox" || vm.state.tab !== "clients") ? "clients" : undefined;
  const tabTag: MailTabDef | undefined = tagId === undefined ? undefined : vm.state.tabDefs.find((d) => d.id === tagId);
  const sender = (
    <Stack direction="horizontal" align="center" gap="1.5">
      <Text weight={weight} lines={1}>
        {name}
      </Text>
      {tabTag !== undefined && wide ? <Chip label={Mail.mailTabLabel(tabTag, locale)} tone={mailTabTone(tabTag)} /> : null}
    </Stack>
  );
  const subjectText = row.subject === "" ? t("mail.noSubject") : row.subject;
  const rowLabels = labels.filter((l) => (row.thread.labelIds ?? []).includes(l.id));
  // A client's one open project (People cards), after the tab tag.
  const project = useThreadProject(row.thread.tab === "clients" ? first : undefined);
  const code = Mail.mailCodeView(row.thread, now);
  const hasFlags = (tabTag !== undefined && !wide) || project !== undefined || rowLabels.length > 0 || row.needsReply || row.phishing || code?.code != null || pills.length > 0;
  const pillChips = pills.map((p) => <Chip key={`${p.tone}${p.label}`} label={p.label} tone={p.tone === "group" ? labelTone("orange") : "neutral"} />);
  const flags = (
    <>
      {wide ? null : pillChips}
      {tabTag !== undefined && !wide ? <Chip label={Mail.mailTabLabel(tabTag, locale)} tone={mailTabTone(tabTag)} /> : null}
      {project === undefined ? null : <ThreadProjectChip from={first} still testID={`mail-project-${id}`} />}
      {rowLabels.map((l) => (
        <Chip key={l.id} label={l.name} tone={labelTone(l.color)} />
      ))}
      {row.priority === "reminder" ? (
        <Chip label={t("mail.priority.reminder", { count: noReplyDays(row.thread, now) })} tone="warning" icon="clock" />
      ) : row.needsReply ? (
        <Chip label={t("mail.needsReply")} tone="warning" />
      ) : null}
      {row.phishing ? <Icon name="warning" size="icon.sm" color="status.danger.fg" /> : null}
      {code?.code == null ? null : <MailCodeChip code={code.copyText} expiresAt={code.expiresAt} onCopy={(text) => navigator.clipboard.writeText(text)} testID={`mail-code-${id}`} />}
    </>
  );
  // The date never wraps or shrinks (the sender and subject truncate instead); the hover actions take its place.
  const meta = (
    <Stack direction="horizontal" align="center" gap="1.5" fixedSize hideOnHover testID={`mail-meta-${id}`}>
      {row.hasAttachments ? <Icon name="attach" size="icon.sm" color="text.tertiary" /> : null}
      <Text variant="footnote" weight={row.unread ? "semibold" : "regular"} color={row.unread ? "text.primary" : "text.secondary"} lines={1} testID={`mail-date-${id}`}>
        {folder === "snoozed" && row.thread.snoozedUntil !== undefined
          ? t("mail.snoozedUntil", { time: mailShortDate(row.thread.snoozedUntil, locale) })
          : folder === "scheduled" && row.thread.scheduledAt !== undefined
            ? t("mail.scheduledFor", { time: mailShortDate(row.thread.scheduledAt, locale) })
            : mailShortDate(row.lastAt, locale)}
      </Text>
      {row.unread ? <Badge dot tone="accent" /> : null}
    </Stack>
  );
  const background = selected ? "bg.selected" : arriving ? "accent.subtle" : cursor ? "bg.highlight" : code?.code != null ? "accent.subtle" : row.unread ? "bg.canvas" : "bg.sunken";
  return (
    // One row, tinted as a whole on hover (star included); on hover its actions show in place of the date (Gmail), over
    // the row's end rather than beside it, so they never take room from (or land on) the sender and subject.
    <Stack direction="horizontal" align="center" gap="1" paddingX="2" background={background} fadeBackground hoverHighlight hoverGroup keepInView={cursor && follow} testID={`mail-row-${id}`}>
      {canChange ? (
        <>
          <Checkbox compact accessibilityLabel={t("mail.select", { subject: row.subject })} value={selected} onChange={() => vm.toggleSelected(id)} testID={`mail-select-${id}`} />
          <IconButton icon={row.starred ? "star" : "starOff"} size="sm" accessibilityLabel={row.starred ? t("mail.action.unstar") : t("mail.action.star")} onPress={() => vm.setStarred(id, !row.starred)} testID={`mail-star-${id}`} />
          <PriorityMarker t={t} thread={row.thread} now={now} onToggle={togglePriority} inRow testID={`mail-priority-${id}`} />
        </>
      ) : null}
      <Stack grow>
      <Pressable bare direction="horizontal" onPress={open} contextMenu={[...(nav.canSplit ? [newTab] : []), ...(canChange ? [...(nav.canSplit ? [{ id: "sep-tab", separator: true as const }] : []), ...hover, { id: "sep-ctx", separator: true as const }, ...more] : [])]} testID={`mail-open-${id}`}>
        {wide ? (
          <Stack grow direction="horizontal" align="center" gap="3" paddingY="2">
            <Avatar name={name} src={avatarSrc} size="avatar.sm" />
            <Stack width="property.label">{sender}</Stack>
            <Stack grow direction="horizontal" align="center" gap="1.5">
              {flags}
              <Stack grow>
                <Text weight={row.unread ? "semibold" : "regular"} lines={1} testID={`mail-subject-${id}`}>
                  {row.snippet === "" ? subjectText : `${subjectText} — ${row.snippet}`}
                </Text>
              </Stack>
            </Stack>
            {pillChips}
            {meta}
          </Stack>
        ) : (
          <Stack grow direction="horizontal" align="start" gap="3" paddingY="2.5">
            <Avatar name={name} src={avatarSrc} size="avatar.lg" />
            <Stack grow gap="0.5">
              <Stack direction="horizontal" align="center" gap="2">
                <Stack grow>{sender}</Stack>
                {meta}
              </Stack>
              <Text weight={row.unread ? "semibold" : "regular"} lines={1} testID={`mail-subject-${id}`}>
                {subjectText}
              </Text>
              {row.snippet === "" ? null : (
                <Text variant="footnote" color="text.secondary" lines={1}>
                  {row.snippet}
                </Text>
              )}
              {hasFlags ? (
                <Stack direction="horizontal" align="center" gap="1.5" wrap>
                  {flags}
                </Stack>
              ) : null}
            </Stack>
          </Stack>
        )}
      </Pressable>
      </Stack>
      {/* Over the date's line (stacked rows: the first line), opaque only behind the buttons. */}
      {canChange ? (
        <Stack direction="horizontal" align="center" paddingX="1" paddingY={wide ? undefined : "2"} overlay={wide ? "end" : "top-end"} revealOnHover testID={`mail-row-actions-${id}`}>
          <Stack direction="horizontal" align="center" gap="0.5" paddingX="1" background={background}>
            {hover.map((h) => (
              <IconButton key={h.id} icon={h.icon} size="sm" accessibilityLabel={h.label} onPress={h.onSelect} testID={`mail-row-${h.id}-${id}`} />
            ))}
            <Menu items={more} align="end" testID={`mail-row-more-${id}`}>
              <IconButton icon="more" size="sm" accessibilityLabel={t("mail.more")} onPress={() => undefined} testID={`mail-row-more-button-${id}`} />
            </Menu>
          </Stack>
        </Stack>
      ) : null}
    </Stack>
  );
}
